import {GetValues, ModifyString, Range} from "js-vextensions";
import {AddSchema} from "mobx-graphlink";
import {StatView} from "./@StatView.js";
import {GetLeftEdgeDayOffsetForDayRange} from "../../../UI/Stats/Helpers.js";

export const StatsXOrYType_entries = [
	"dreamSegments_sum",
	"lucids_sum",
	"responseTime",
	"linkVoicings_sum",
	"linkVisualizations_sum",
	"realityChecks_sum",
	"frameDisplays_sum",
] as const;
export type StatsXOrYType = typeof StatsXOrYType_entries[number];

export const StatsXType_entries = [
	"showAll",
	"dayOffset",
	"dayInGroup",
	"cycleInGroup",
	"cycleInNight",
	"startHour",
	"endHour",
	"cycle1EndHour",
	"cycleLastStartHour",
	"frameDisplays_sum_last24Hours",
	"frameDisplays_sincePrevNight_sum",
	"memoryPrompts_sincePrevNight_sum", // memory-palace comp will probably be renamed to just memory comp in future
	"visPrompts_sincePrevNight_sum",
	...StatsXOrYType_entries,
] as const;
export type StatsXType = typeof StatsXType_entries[number];

export class TickStepInfo {
	constructor(data?: Partial<TickStepInfo>) { Object.assign(this, data); }
	xTicks_initial?: (view: StatView)=>number[];
	/** If true, X additional x-ticks will get processed beyond edge of leftmost visible x-tick, where X is based on smoothing range. */
	xTicks_prependToInitialForSmoothing?: boolean;
	xTicks_lockToInitial?: boolean;

	xTicks_override?: number[];
	xTicks_stepOverride?: number;
}
export const StatsXType_tickStepInfos = new Map<StatsXType, TickStepInfo>([
	["showAll", new TickStepInfo({
		xTicks_override: [0],
	})],
	["responseTime", new TickStepInfo({
		xTicks_override: [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1_000, 10_000, 100_000],
	})],
	["linkVoicings_sum", new TickStepInfo({
		xTicks_stepOverride: 10,
	})],
	["linkVisualizations_sum", new TickStepInfo({
		xTicks_stepOverride: 10,
	})],
	["realityChecks_sum", new TickStepInfo({
		xTicks_stepOverride: 5,
	})],
	["frameDisplays_sum", new TickStepInfo({
		xTicks_stepOverride: 10,
	})],
	["frameDisplays_sum_last24Hours", new TickStepInfo({
		xTicks_stepOverride: 50,
	})],
	["frameDisplays_sincePrevNight_sum", new TickStepInfo({
		xTicks_stepOverride: 50,
	})],
	["memoryPrompts_sincePrevNight_sum", new TickStepInfo({
		xTicks_stepOverride: 50,
	})],
	["visPrompts_sincePrevNight_sum", new TickStepInfo({
		xTicks_stepOverride: 50,
	})],
	["dayOffset", new TickStepInfo({
		xTicks_initial: view=>{
			if (view.dayRange_type == null || view.dayRange_start == null || view.dayRange_count == null || view.days_omitLastX == null) return [];
			const leftDayOffset = GetLeftEdgeDayOffsetForDayRange(view.dayRange_type, view.dayRange_start, view.dayRange_count);
			return Range(leftDayOffset, 0 - view.days_omitLastX, 1);
		},
		xTicks_prependToInitialForSmoothing: true,
		xTicks_lockToInitial: true,
	})],
	["dayInGroup", new TickStepInfo({
		xTicks_initial: view=>{
			if (view.dayRange_count == null) return [];
			return Range(-view.dayRange_count, 0, 1);
		},
		xTicks_prependToInitialForSmoothing: true,
		xTicks_lockToInitial: true,
	})],
	["cycleInGroup", new TickStepInfo({
		xTicks_initial: view=>{
			if (view.cyclesToShow == null) return [];
			return Range(-view.cyclesToShow, 0, 1);
		},
		xTicks_prependToInitialForSmoothing: true,
		xTicks_lockToInitial: true,
	})],
	["cycleInNight", new TickStepInfo({
		xTicks_initial: view=>Range(1, 10, 1),
	})],
	["startHour", new TickStepInfo({
		xTicks_stepOverride: 1, // not really needed (since we get hour using Date.getHours(), which is an integer), but just to be sure (and clarify intent)
	})],
	["endHour", new TickStepInfo({
		xTicks_stepOverride: 1, // not really needed, but see above
	})],
	["cycle1EndHour", new TickStepInfo({
		xTicks_stepOverride: 1, // not really needed, but see above
	})],
	["cycleLastStartHour", new TickStepInfo({
		xTicks_stepOverride: 1, // not really needed, but see above
	})],
]);

export const StatsYType_entries = [
	...StatsXOrYType_entries,
	"combined",
	"statNights_sum",
	"dreamsMatching_sum",
	"lucids_perCycle",
	"lucids_perNight",
	"termsInShortText",
	"termsInShortText_sum",
	"termsInLongText",
	"termsInLongText_sum",
	"visualizationPrompts_sum",
	"quizPrompts_sum",
	//"quizTryHitPercent",
	"quizFirstTryHitPercent",
	"quizTimeTillSuccess",
	"memoryPalacePrompts_sum",
	"memoryPalacePromptRecallPercent",
	"memoryPalacePromptDecodeTime_median",
	"memoryPalacePromptEncodeTime_median",
	"mapObjects_sum_cumulative",
] as const;
export type StatsYType = typeof StatsYType_entries[number];

// todo: probably change UI to show "tag" instead of "entity" everywhere (more common/understandable term)
export function StatType_Label(type: StatsXType | StatsYType) {
	// x-or-y-types
	if (type == "dreamSegments_sum") return "Dream segments (sum)";
	if (type == "lucids_sum") return "Lucids (sum)";
	if (type == "responseTime") return "Response time [s] (typical)";
	if (type == "linkVoicings_sum") return "Link voicings (sum)";
	if (type == "linkVisualizations_sum") return "Link visualizations (sum)";
	if (type == "realityChecks_sum") return "Reality checks (sum)";
	if (type == "frameDisplays_sum") return "Frame displays (sum)";

	// x-types
	if (type == "showAll") return "(show all)";
	if (type == "dayOffset") return "Day offset";
	if (type == "dayInGroup") return "Day (in group)";
	if (type == "cycleInGroup") return "Cycle (in group)";
	if (type == "cycleInNight") return "Cycle (in night)";
	if (type == "startHour") return "Start hour";
	if (type == "endHour") return "End hour";
	if (type == "cycle1EndHour") return "Cycle-1 end hour";
	if (type == "cycleLastStartHour") return "Cycle-last start hour";
	if (type == "frameDisplays_sum_last24Hours") return "Frame displays, last 24 hours (sum)";
	if (type == "frameDisplays_sincePrevNight_sum") return "Frame displays, since prev. night (sum)";
	if (type == "memoryPrompts_sincePrevNight_sum") return "MP prompts, since prev. night (sum)";
	if (type == "visPrompts_sincePrevNight_sum") return "Vis. prompts, since prev. night (sum)";

	// y-types
	if (type == "combined") return "Combined";
	if (type == "dreamsMatching_sum") return "Dreams matching (sum)";
	if (type == "statNights_sum") return "Nights (sum)";
	if (type == "lucids_perCycle") return "Lucids (per cycle)";
	if (type == "lucids_perNight") return "Lucids (per night)";
	if (type == "termsInShortText") return "Tags (typical)";
	if (type == "termsInShortText_sum") return "Tags (sum)";
	if (type == "termsInLongText") return "Words (typical)";
	if (type == "termsInLongText_sum") return "Words (sum)";
	if (type == "visualizationPrompts_sum") return "Visualization prompts (sum)";
	if (type == "quizPrompts_sum") return "Quiz prompts (sum)";
	//if (type == "quizTryHitPercent") return "Quiz try hit-% (typical)";
	if (type == "quizFirstTryHitPercent") return "Quiz first-try hit-% (typical)";
	if (type == "quizTimeTillSuccess") return "Quiz time-till-success [s] (typical)";
	if (type == "memoryPalacePrompts_sum") return "MPP (memory-palace prompt) count (sum)";
	if (type == "memoryPalacePromptRecallPercent") return "MPP recall-% (typical)";
	if (type == "memoryPalacePromptDecodeTime_median") return "MPP decode time [s] (median)";
	if (type == "memoryPalacePromptEncodeTime_median") return "MPP encode time [s] (median)";
	if (type == "mapObjects_sum_cumulative") return "Map objects (cumulative sum)";

	return ModifyString(type, m=>[m.startLower_to_upper, m.lowerUpper_to_lowerSpaceLower]);
}

export enum StatsGrouping {
	none = "none",
	variant = "variant",
	alarmSequence = "alarmSequence",
	alarmDelay = "alarmDelay",
	volumeMod = "volumeMod",
	alarmRestartIntervalMod = "alarmRestartIntervalMod",
	memoryPalaceTarget = "memoryPalaceTarget",
}
AddSchema("StatsGrouping", {enum: GetValues(StatsGrouping)});